import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { App as CapacitorApp } from '@capacitor/app';
import { Camera, Check, ChevronLeft, Crosshair, Loader2, MapPin, Mic, Pause, Play, Square, Trash2, X } from 'lucide-react';
import { fetchCurrentUser } from '@/lib/auth';
import { useVisitaBundle, useVisitaBundles, useSyncVisita } from '../api';
import { estimarEstadio } from '../phenology';
import { tirarFoto, pegarLocalizacao, resolverTalhao } from '../capture';
import {
  addFoto,
  descartarVisita,
  fotosDaVisita,
  rascunhoDaFazenda,
  updateFoto,
  upsertVisita,
  type OcorrenciaCampo,
  type PendingAudio,
  type PendingVisita,
  type VisitaStatus,
} from '../offline';
import { iniciarGravacao, suportaGravacao, type GravacaoAtiva } from '../audio';
import { OfflineMap } from '@/modules/agronomia/components/OfflineMap';

interface FotoUI {
  clientUuid: string;
  // Ponto marcado sem foto: sem url de imagem.
  url: string | null;
  recomendacao: string;
  audios: { clientUuid: string; durMs: number | null }[];
}
interface PontoUI {
  uuid: string;
  lat: number | null;
  lon: number | null;
  talhaoId: number | null;
  talhaoNumero: string | null;
  // Rótulo do talhão como o resto do sistema mostra: nome do KML/edição quando
  // existe, senão "Talhão <numero>". Antes o card mostrava só o número e
  // divergia da fazenda, do mapa e do relatório.
  talhaoRotulo: string | null;
  cultivar: string | null;
  estadio: string | null;
  fotos: FotoUI[];
  // Ocorrências ficam na 1ª foto/registro do ponto (é o que o sync manda).
  ocorrencias: OcorrenciaCampo[];
}

// Distância aproximada em metros (equiretangular — ok pra poucos metros).
function distM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = (((bLon - aLon) * Math.PI) / 180) * Math.cos(((aLat + bLat) / 2 * Math.PI) / 180);
  return Math.sqrt(dLat * dLat + dLon * dLon) * R;
}
const RAIO_PONTO_M = 25;

// Tela 11 · Nova visita técnica — captura MULTI-PONTO. Cada registro resolve
// talhão + GPS; registros a ≤25 m de um ponto existente entram nele, senão viram
// ponto novo. O ponto pode nascer de uma foto ou de "marcar ponto sem foto".
// Anotações e áudios (vários) são por registro; ocorrências são do ponto.
// Finaliza quando o agrônomo decide.
export function NovaVisita({ imovelId, imovelIds }: { imovelId?: number; imovelIds?: number[] }) {
  const navigate = useNavigate();
  // Visita de uma fazenda (tela da fazenda) ou de várias (tela do produtor).
  const multi = imovelIds != null && imovelIds.length > 0;
  const ids = multi ? imovelIds : (imovelId != null ? [imovelId] : []);
  const principal = ids[0] ?? 0;
  const bundleUm = useVisitaBundle(multi ? null : (imovelId ?? null));
  const bundleVarios = useVisitaBundles(multi ? imovelIds : []);
  const bundleQ = multi ? bundleVarios : bundleUm;
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });
  const sync = useSyncVisita();

  const [visitaUuid, setVisitaUuid] = useState<string>(() => crypto.randomUUID());
  const [dataVisita, setDataVisita] = useState(() => new Date().toISOString());
  const [pontos, setPontos] = useState<PontoUI[]>([]);
  const [capturing, setCapturing] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [gravandoFoto, setGravandoFoto] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<PendingVisita | null>(null);
  const recRef = useRef<GravacaoAtiva | null>(null);

  const fazenda = bundleQ.data?.fazenda ?? null;
  const fazendaNome = multi
    ? `${ids.length} ${ids.length === 1 ? 'fazenda' : 'fazendas'}`
    : (fazenda?.fazenda ?? 'Fazenda');
  const totalFotos = pontos.reduce((s, p) => s + p.fotos.length, 0);

  // Rótulo do talhão pelo bundle (retomada de rascunho, que só guardou o id).
  function rotuloDoTalhao(talhaoId: number | null, numero: string | null): string | null {
    const t = (bundleQ.data?.talhoes ?? []).find((x) => x.talhao_id === talhaoId);
    if (t) return t.nome || `Talhão ${t.numero}`;
    return numero ? `Talhão ${numero}` : null;
  }

  // Numeração DENTRO do talhão: talhão 1 tem pontos 1..n, o talhão seguinte
  // volta pro 1 (é assim que o relatório e os pinos do mapa numeram).
  const indicePorTalhao = useMemo(() => {
    const contador = new Map<string, number>();
    const m = new Map<string, number>();
    for (const p of pontos) {
      const k = p.talhaoNumero ?? '—';
      const n = (contador.get(k) ?? 0) + 1;
      contador.set(k, n);
      m.set(p.uuid, n);
    }
    return m;
  }, [pontos]);

  // ── Trava de saída ─────────────────────────────────────────────────────────
  // Com fotos capturadas, QUALQUER volta abre o sheet de confirmação em vez de
  // descartar o trabalho de campo. Três camadas, porque cada gesto chega por um
  // caminho diferente:
  //  1. Botão ‹ da tela → pedirSaida() direto (sem depender de history).
  //  2. Botão físico/gesto do Android → listener 'backButton' do Capacitor
  //     (registrado só enquanto esta tela vive; com listener o Capacitor NÃO
  //     faz o WebView.goBack() nativo — nós decidimos).
  //  3. Gesto de voltar do browser (web) → entrada-sentinela no history +
  //     popstate. O sentinela é armado DENTRO do clique de captura/retomada:
  //     pushState sem gesto do usuário é "pulável" pela History Manipulation
  //     Intervention do Chrome — foi por isso que a v2.82 falhou no aparelho.
  const [confirmSair, setConfirmSair] = useState(false);
  const confirmSairRef = useRef(false);
  confirmSairRef.current = confirmSair;
  const totalFotosRef = useRef(0);
  totalFotosRef.current = totalFotos;
  const saindoRef = useRef(false);
  const armadoRef = useRef(false);

  // Arma o sentinela (1 entrada duplicada, mesma URL → router quieto). Chamar
  // SEMPRE no início síncrono de um clique, enquanto há user activation.
  function armarSentinela() {
    if (armadoRef.current) return;
    armadoRef.current = true;
    window.history.pushState(window.history.state, '', window.location.href);
  }

  function pedirSaida() {
    if (totalFotosRef.current > 0) setConfirmSair(true);
    else window.history.back();
  }

  useEffect(() => {
    function onPop() {
      if (saindoRef.current) return;
      if (totalFotosRef.current > 0) {
        // Gesto de voltar consumiu o sentinela: re-arma e pergunta (ou fecha o
        // sheet, se já estava aberto — convenção do voltar do Android).
        window.history.pushState(window.history.state, '', window.location.href);
        armadoRef.current = true;
        setConfirmSair(!confirmSairRef.current);
      } else if (armadoRef.current) {
        // Sentinela armado mas sem conteúdo (câmera cancelada): completa a saída.
        armadoRef.current = false;
        saindoRef.current = true;
        window.history.back();
      }
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Voltar físico do Android (APK). No web o evento nunca dispara.
  useEffect(() => {
    const sub = CapacitorApp.addListener('backButton', () => {
      if (confirmSairRef.current) setConfirmSair(false);
      else if (totalFotosRef.current > 0) setConfirmSair(true);
      else window.history.back();
    });
    return () => {
      void sub.then((h) => h.remove()).catch(() => {});
    };
  }, []);

  // Saída autorizada pelo sheet: consome sentinela (se armado) + entrada da
  // visita, voltando pra tela de onde o agrônomo veio (fazenda ou talhão).
  function sairDaTela() {
    saindoRef.current = true;
    setConfirmSair(false);
    window.history.go(armadoRef.current ? -2 : -1);
  }

  // Visita pausada nesta fazenda → oferece retomar ao entrar de novo.
  useEffect(() => {
    let vivo = true;
    void rascunhoDaFazenda(principal).then((r) => {
      if (vivo && r && r.clientUuid !== visitaUuid) setRascunho(r);
    });
    return () => {
      vivo = false;
    };
  }, [principal, visitaUuid]);

  function buildVisita(status: VisitaStatus): PendingVisita {
    return {
      clientUuid: visitaUuid,
      imovelId: principal,
      imovelIds: ids,
      fazendaNome: fazenda?.fazenda ?? null,
      produtorNome: fazenda?.produtor ?? null,
      agronomoNome: me?.name ?? null,
      dataVisita,
      observacoes: '',
      status,
      createdAt: Date.now(),
      syncedVisitaId: null,
    };
  }

  // Pausar: a visita já está toda no aparelho (fotos/áudios gravam no Dexie a
  // cada captura) — só garante o cabeçalho como rascunho (fora do sync) e sai.
  async function handlePausar() {
    await upsertVisita(buildVisita('rascunho'));
    sairDaTela();
  }

  async function handleDescartar() {
    await descartarVisita(visitaUuid);
    sairDaTela();
  }

  // Retomar visita pausada: recarrega fotos do Dexie e remonta os pontos.
  async function handleRetomar() {
    if (!rascunho) return;
    // Síncrono, ainda dentro do clique: sentinela com user activation.
    armarSentinela();
    const fotos = await fotosDaVisita(rascunho.clientUuid);
    const grupos = new Map<string, PontoUI>();
    for (const f of fotos) {
      const key = f.pontoUuid ?? f.clientUuid;
      let p = grupos.get(key);
      if (!p) {
        p = {
          uuid: key,
          lat: f.lat,
          lon: f.lon,
          talhaoId: f.talhaoId,
          talhaoNumero: f.talhaoNumero,
          talhaoRotulo: rotuloDoTalhao(f.talhaoId, f.talhaoNumero),
          cultivar: null,
          estadio: f.estadio,
          fotos: [],
          ocorrencias: [],
        };
        grupos.set(key, p);
      }
      if (f.ocorrencias && f.ocorrencias.length) p.ocorrencias = f.ocorrencias;
      p.fotos.push({
        clientUuid: f.clientUuid,
        url: f.blob ? URL.createObjectURL(f.blob) : null,
        recomendacao: f.recomendacao,
        audios: (f.audios ?? []).map((a) => ({ clientUuid: a.clientUuid, durMs: a.durMs })),
      });
    }
    setVisitaUuid(rascunho.clientUuid);
    setDataVisita(rascunho.dataVisita);
    setPontos([...grupos.values()]);
    setRascunho(null);
  }

  async function handleDescartarRascunho() {
    if (!rascunho) return;
    await descartarVisita(rascunho.clientUuid);
    setRascunho(null);
  }

  // Resolve (ou cria) o ponto onde o agrônomo está: registros a ≤25 m entram no
  // mesmo ponto. Usado tanto pela foto quanto pelo ponto marcado sem foto.
  function acharOuCriarPonto(coords: { lat: number; lon: number } | null, capturadoEm: string): PontoUI {
    // O talhão sai SEMPRE do GPS desta captura. Antes o ponto era reaproveitado
    // só pela distância, e na divisa de dois talhões os 25 m atravessavam a
    // linha — a foto herdava o talhão do ponto anterior, que era o errado.
    const tal = coords ? resolverTalhao(bundleQ.data?.talhoes ?? [], coords.lon, coords.lat) : null;
    const achado =
      coords != null
        ? pontos.find(
          (p) =>
            p.lat != null && p.lon != null
            && distM(p.lat, p.lon, coords.lat, coords.lon) <= RAIO_PONTO_M
            // só junta no mesmo ponto se for o mesmo talhão
            && (p.talhaoId ?? null) === (tal?.talhao_id ?? null),
        )
        : undefined;
    if (achado) return achado;
    const est = tal
      ? estimarEstadio({ dataEmergencia: tal.data_emergencia, dataPlantio: tal.data_plantio, dataVisita: capturadoEm, cicloDias: tal.ciclo_dias ?? null })
      : null;
    // O ponto é o grupo de registros com este pontoUuid; anotações e áudios são
    // por registro, ocorrências ficam no 1º registro do ponto.
    const novo: PontoUI = {
      uuid: crypto.randomUUID(),
      lat: coords?.lat ?? null,
      lon: coords?.lon ?? null,
      talhaoId: tal?.talhao_id ?? null,
      talhaoNumero: tal?.numero ?? null,
      talhaoRotulo: tal ? (tal.nome || `Talhão ${tal.numero}`) : null,
      cultivar: tal?.variedade ?? null,
      estadio: est?.estadio ?? null,
      fotos: [],
      ocorrencias: [],
    };
    setPontos((prev) => [...prev, novo]);
    return novo;
  }

  async function handleCapture() {
    if (capturing) return;
    // Síncrono, ainda dentro do clique: sentinela com user activation.
    armarSentinela();
    setErro(null);
    setCapturing(true);
    try {
      const f = await tirarFoto();
      if (!f) return;
      const capturadoEm = new Date().toISOString();
      const coords = await pegarLocalizacao();
      // Rascunho enquanto captura: fica fora do sync até o "Finalizar visita".
      await upsertVisita(buildVisita('rascunho'));

      const pAtual = acharOuCriarPonto(coords, capturadoEm);
      const clientUuid = crypto.randomUUID();
      await addFoto({
        clientUuid,
        visitaUuid,
        pontoUuid: pAtual.uuid,
        blob: f.blob,
        mime: f.mime,
        filename: f.filename,
        lat: coords?.lat ?? null,
        lon: coords?.lon ?? null,
        capturadoEm,
        talhaoId: pAtual.talhaoId,
        talhaoNumero: pAtual.talhaoNumero,
        legenda: pAtual.talhaoRotulo ? [pAtual.talhaoRotulo] : ['Talhão não identificado'],
        estadio: pAtual.estadio,
        recomendacao: '',
        audios: [],
        ocorrencias: [],
        synced: false,
        createdAt: Date.now(),
      });
      const url = URL.createObjectURL(f.blob);
      setPontos((prev) =>
        prev.map((p) =>
          p.uuid === pAtual.uuid
            ? { ...p, fotos: [...p.fotos, { clientUuid, url, recomendacao: '', audios: [] }] }
            : p,
        ),
      );
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao capturar foto');
    } finally {
      setCapturing(false);
    }
  }

  // Marcar ponto SEM foto: pega GPS + talhão e abre o card pra anotar/registrar
  // ocorrência. A foto não é obrigatória pra o ponto existir no relatório.
  async function handleMarcarPonto() {
    if (capturing) return;
    armarSentinela();
    setErro(null);
    setCapturing(true);
    try {
      const capturadoEm = new Date().toISOString();
      const coords = await pegarLocalizacao();
      await upsertVisita(buildVisita('rascunho'));
      const pAtual = acharOuCriarPonto(coords, capturadoEm);
      const clientUuid = crypto.randomUUID();
      await addFoto({
        clientUuid,
        visitaUuid,
        pontoUuid: pAtual.uuid,
        blob: null,
        semFoto: true,
        mime: '',
        filename: '',
        lat: coords?.lat ?? null,
        lon: coords?.lon ?? null,
        capturadoEm,
        talhaoId: pAtual.talhaoId,
        talhaoNumero: pAtual.talhaoNumero,
        legenda: pAtual.talhaoRotulo ? [pAtual.talhaoRotulo] : ['Talhão não identificado'],
        estadio: pAtual.estadio,
        recomendacao: '',
        audios: [],
        ocorrencias: [],
        synced: false,
        createdAt: Date.now(),
      });
      setPontos((prev) =>
        prev.map((p) =>
          p.uuid === pAtual.uuid
            ? { ...p, fotos: [...p.fotos, { clientUuid, url: null, recomendacao: '', audios: [] }] }
            : p,
        ),
      );
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao marcar o ponto');
    } finally {
      setCapturing(false);
    }
  }

  // Ocorrências do ponto: guardadas no PRIMEIRO registro do ponto (é de lá que o
  // sync as manda) e gravadas no aparelho na hora.
  async function handleOcorrencias(pontoUuid: string, itens: OcorrenciaCampo[]) {
    const ponto = pontos.find((p) => p.uuid === pontoUuid);
    const primeira = ponto?.fotos[0];
    if (!primeira) return;
    setPontos((prev) => prev.map((p) => (p.uuid === pontoUuid ? { ...p, ocorrencias: itens } : p)));
    await updateFoto(primeira.clientUuid, { ocorrencias: itens });
  }

  // Texto e áudio são POR FOTO (não por ponto): gravam direto na foto ativa.
  // Salva no aparelho a cada tecla (pra nada se perder se o app for pro fundo) —
  // o botão "Salvar anotação" do card é a confirmação visível disso.
  function handleRecomendacao(pontoUuid: string, fotoUuid: string, text: string) {
    setPontos((prev) =>
      prev.map((p) =>
        p.uuid === pontoUuid
          ? { ...p, fotos: p.fotos.map((f) => (f.clientUuid === fotoUuid ? { ...f, recomendacao: text } : f)) }
          : p,
      ),
    );
    void updateFoto(fotoUuid, { recomendacao: text });
  }

  async function handleSalvarAnotacao(fotoUuid: string, text: string) {
    await updateFoto(fotoUuid, { recomendacao: text });
    await upsertVisita(buildVisita('rascunho'));
  }

  async function handleGravarAudio(fotoUuid: string) {
    if (!suportaGravacao()) {
      setErro('Gravação de áudio não suportada neste aparelho.');
      return;
    }
    setErro(null);
    try {
      recRef.current = await iniciarGravacao();
      setGravandoFoto(fotoUuid);
    } catch (e) {
      const name = e instanceof DOMException ? e.name : '';
      if (name === 'NotFoundError') setErro('Microfone não encontrado no aparelho.');
      else setErro('Microfone bloqueado. Libere o microfone nas permissões do app e tente de novo.');
    }
  }

  // Cada gravação ACRESCENTA um áudio à foto (o agrônomo volta na mesma imagem
  // e grava outra observação) — a lista vive em PendingFoto.audios.
  async function handlePararAudio(pontoUuid: string, fotoUuid: string) {
    const rec = recRef.current;
    recRef.current = null;
    setGravandoFoto(null);
    if (!rec) return;
    const out = await rec.stop();
    if (!out) return;
    const novo: PendingAudio = {
      clientUuid: crypto.randomUUID(),
      blob: out.blob,
      mime: out.mime,
      filename: out.filename,
      durMs: out.durMs,
    };
    const atuais = pontos.find((p) => p.uuid === pontoUuid)?.fotos.find((f) => f.clientUuid === fotoUuid)?.audios ?? [];
    const lista = await lerAudios(fotoUuid, atuais.length);
    await updateFoto(fotoUuid, { audios: [...lista, novo] });
    setPontos((prev) =>
      prev.map((p) =>
        p.uuid === pontoUuid
          ? {
            ...p,
            fotos: p.fotos.map((f) =>
              f.clientUuid === fotoUuid
                ? { ...f, audios: [...f.audios, { clientUuid: novo.clientUuid, durMs: novo.durMs }] }
                : f),
          }
          : p,
      ),
    );
  }

  // Os blobs dos áudios já gravados ficam só no Dexie (não no estado da tela),
  // então precisam ser relidos antes de acrescentar mais um.
  async function lerAudios(fotoUuid: string, esperados: number): Promise<PendingAudio[]> {
    if (!esperados) return [];
    const fotos = await fotosDaVisita(visitaUuid);
    return fotos.find((f) => f.clientUuid === fotoUuid)?.audios ?? [];
  }

  async function handleRemoverAudio(pontoUuid: string, fotoUuid: string, audioUuid: string) {
    const fotosDb = await fotosDaVisita(visitaUuid);
    const atuais = fotosDb.find((f) => f.clientUuid === fotoUuid)?.audios ?? [];
    await updateFoto(fotoUuid, { audios: atuais.filter((a) => a.clientUuid !== audioUuid) });
    setPontos((prev) =>
      prev.map((p) =>
        p.uuid === pontoUuid
          ? { ...p, fotos: p.fotos.map((f) => (f.clientUuid === fotoUuid ? { ...f, audios: f.audios.filter((a) => a.clientUuid !== audioUuid) } : f)) }
          : p,
      ),
    );
  }

  async function handleFinalizar() {
    if (totalFotos === 0) {
      setErro('Tire ao menos uma foto da visita.');
      return;
    }
    setErro(null);
    setFinalizando(true);
    try {
      // Sempre salva a visita + fotos no aparelho (offline-first).
      await upsertVisita(buildVisita('pendente'));
      // Online: já tenta sincronizar. Offline (ou se a rede falhar): fica pendente
      // e sincroniza depois pelo botão "Sincronizar" da fazenda. Em ambos os casos
      // a visita fica salva e voltamos pra tela da fazenda.
      if (navigator.onLine) {
        try {
          await sync.mutateAsync(visitaUuid);
        } catch {
          /* rede falhou — segue pendente */
        }
      }
      // replace: a fazenda substitui o sentinela — sem entrada morta no history.
      saindoRef.current = true;
      void navigate({ to: '/agronomia/fazendas/$id', params: { id: String(principal) }, replace: true });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar a visita no aparelho.');
    } finally {
      setFinalizando(false);
    }
  }

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex flex-col px-3 pt-4 pb-3 shrink-0">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={pedirSaida}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">
            Nova visita técnica
          </h1>
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2">
          {fazendaNome}
          {pontos.length > 0 &&
            ` · ${pontos.length} ${pontos.length === 1 ? 'ponto' : 'pontos'} · ${totalFotos} ${totalFotos === 1 ? 'foto' : 'fotos'}`}
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-4 px-4 pb-6 pt-3.5">
          {/* Visita pausada nesta fazenda: oferece retomar de onde parou. */}
          {rascunho && pontos.length === 0 && (
            <div className="flex items-center gap-3 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-3">
              <Pause size={18} strokeWidth={2} className="shrink-0 text-accent" />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[0.85rem] font-medium text-text">Visita pausada</span>
                <span className="text-[0.75rem] text-text-2">
                  {new Date(rascunho.dataVisita).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => void handleRetomar()}
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-2 text-[0.8rem] font-semibold text-white"
              >
                <Play size={14} strokeWidth={2.25} /> Retomar
              </button>
              <button
                type="button"
                onClick={() => void handleDescartarRascunho()}
                aria-label="Descartar visita pausada"
                className="flex size-9 shrink-0 items-center justify-center rounded-full text-[var(--danger)] hover:bg-surface3"
              >
                <Trash2 size={17} strokeWidth={2} />
              </button>
            </div>
          )}

          {/* Mapa fixo da fazenda (mesmo da tela da fazenda): pins dos talhões +
              posição GPS do agrônomo pra se orientar em qual talhão está. */}
          <div className="h-[300px] overflow-hidden rounded-2xl bg-surface2">
            <OfflineMap imovelId={principal} className="h-full w-full object-cover" />
          </div>

          <button
            type="button"
            onClick={() => void handleCapture()}
            disabled={capturing}
            className="flex w-full items-center justify-center gap-2.5 rounded-2xl bg-[var(--accent)] py-[15px] text-[0.875rem] font-medium text-white disabled:opacity-60"
          >
            {capturing ? <Loader2 size={20} strokeWidth={2} className="animate-spin" /> : <Camera size={20} strokeWidth={2} />}
            Tirar foto
          </button>
          {/* Ponto sem foto: registra GPS + talhão pra anotar/contar praga. */}
          <button
            type="button"
            onClick={() => void handleMarcarPonto()}
            disabled={capturing}
            className="-mt-1.5 flex w-full items-center justify-center gap-2.5 rounded-2xl border border-[var(--agro-card-border)] py-[13px] text-[0.875rem] font-medium text-text disabled:opacity-60"
          >
            <Crosshair size={18} strokeWidth={2} />
            Marcar ponto sem foto
          </button>

          {pontos.length === 0 ? (
            <div className="rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-10 text-center">
              <p className="text-[0.9rem] font-medium text-text">Nenhum ponto ainda</p>
              <p className="mt-1 text-[0.8rem] text-text-2">
                Cada registro identifica o talhão pelo GPS. Registros próximos viram um ponto só.
              </p>
            </div>
          ) : (
            <p className="text-[0.72rem] font-medium uppercase tracking-[0.04em] text-accent">Pontos da visita</p>
          )}

          {/* mais recente no topo: a numeração segue a ordem de captura (ponto 1
              é o primeiro), mas a lista mostra do último pro primeiro. */}
          {[...pontos].reverse().map((p) => (
            <PontoCard
              key={p.uuid}
              ponto={p}
              indice={indicePorTalhao.get(p.uuid) ?? 1}
              gravandoFoto={gravandoFoto}
              onRecomendacao={(fotoUuid, t) => handleRecomendacao(p.uuid, fotoUuid, t)}
              onSalvarAnotacao={(fotoUuid, t) => handleSalvarAnotacao(fotoUuid, t)}
              onOcorrencias={(itens) => handleOcorrencias(p.uuid, itens)}
              onRemoverAudio={(fotoUuid, audioUuid) => void handleRemoverAudio(p.uuid, fotoUuid, audioUuid)}
              onAudio={(fotoUuid) =>
                gravandoFoto === fotoUuid
                  ? void handlePararAudio(p.uuid, fotoUuid)
                  : void handleGravarAudio(fotoUuid)
              }
            />
          ))}

          {erro && <p className="text-[0.8rem] text-[var(--danger)]">{erro}</p>}

          <button
            type="button"
            onClick={() => void handleFinalizar()}
            disabled={finalizando || totalFotos === 0}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--accent)] py-[15px] text-[0.875rem] font-medium text-white disabled:opacity-60"
          >
            {finalizando && <Loader2 size={18} strokeWidth={2} className="animate-spin" />}
            {finalizando ? 'Finalizando…' : 'Finalizar visita'}
          </button>
        </div>
      </div>

      {/* Sheet de confirmação de saída (tela 11c do Figma). Aparece quando uma
          volta é interceptada com fotos capturadas. */}
      {confirmSair && (
        <div className="fixed inset-0 z-[1200] flex items-end bg-black/45" onClick={() => setConfirmSair(false)}>
          <div
            className="w-full rounded-t-[24px] bg-bg px-5 pb-6 pt-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-surface3" />
            <h2 className="text-[1.1rem] font-semibold text-text">Sair da visita técnica?</h2>
            <p className="mt-1 text-[0.85rem] leading-snug text-text-2">
              Você tem {totalFotos} {totalFotos === 1 ? 'foto capturada' : 'fotos capturadas'} nesta
              visita. Escolha o que fazer antes de sair.
            </p>
            <button
              type="button"
              onClick={() => void handlePausar()}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-[13px] text-[0.9rem] font-semibold text-white"
            >
              <Pause size={17} strokeWidth={2.25} /> Pausar e continuar depois
            </button>
            <button
              type="button"
              onClick={() => setConfirmSair(false)}
              className="mt-3 flex w-full items-center justify-center rounded-full border border-[var(--agro-card-border)] py-[13px] text-[0.9rem] font-medium text-text"
            >
              Continuar a visita
            </button>
            <button
              type="button"
              onClick={() => void handleDescartar()}
              className="mt-4 flex w-full items-center justify-center gap-1.5 py-1 text-[0.88rem] font-semibold text-[var(--danger)]"
            >
              <Trash2 size={15} strokeWidth={2} /> Descartar visita
            </button>
            <p className="mt-1 text-center text-[0.72rem] text-text-2">
              As fotos e áudios desta visita serão apagados
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  'h-10 min-w-0 rounded-[10px] border border-[var(--agro-card-border)] bg-surface px-2.5 text-[0.85rem] text-text ' +
  'placeholder:text-text-2 focus:border-[var(--accent)] focus:outline-none';

function PontoCard({
  ponto,
  indice,
  gravandoFoto,
  onRecomendacao,
  onSalvarAnotacao,
  onOcorrencias,
  onRemoverAudio,
  onAudio,
}: {
  ponto: PontoUI;
  indice: number;
  gravandoFoto: string | null;
  onRecomendacao: (fotoUuid: string, t: string) => void;
  onSalvarAnotacao: (fotoUuid: string, t: string) => Promise<void>;
  onOcorrencias: (itens: OcorrenciaCampo[]) => void;
  onRemoverAudio: (fotoUuid: string, audioUuid: string) => void;
  onAudio: (fotoUuid: string) => void;
}) {
  const [ativa, setAtiva] = useState(0);
  const [salvo, setSalvo] = useState(false);
  const n = ponto.fotos.length;
  // Foto nova no ponto → o card já mostra ela (é a que o agrônomo acabou de tirar).
  const antes = useRef(n);
  useEffect(() => {
    if (n > antes.current) setAtiva(n - 1);
    antes.current = n;
  }, [n]);
  const i = Math.min(ativa, Math.max(n - 1, 0));
  const principal = ponto.fotos[i];
  const gravando = principal != null && gravandoFoto === principal.clientUuid;
  const imgSrc = principal?.url ?? null;
  const passo = (d: number) => { setAtiva((v) => (Math.min(v, n - 1) + d + n) % n); setSalvo(false); };

  return (
    <div className="overflow-hidden rounded-[12px] border border-[var(--agro-card-border)] bg-surface2">
      {/* imagem do registro ativo — sem corte, na proporção da captura */}
      {imgSrc && (
        <div className="relative">
          {/* src é objectURL dinâmico — permitido */}
          <img src={imgSrc} alt="" className="block max-h-[380px] w-full object-contain" />
          {n > 1 && (
            <>
              <button
                type="button"
                aria-label="Anterior"
                onClick={() => passo(-1)}
                className="absolute left-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-[1.3rem] leading-none text-white"
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Próxima"
                onClick={() => passo(1)}
                className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-[1.3rem] leading-none text-white"
              >
                ›
              </button>
              <span className="absolute right-2.5 top-2.5 rounded-full bg-black/60 px-2.5 py-1 text-[0.7rem] font-medium text-white">
                {i + 1} / {n}
              </span>
            </>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2.5 px-3.5 pb-3.5 pt-3">
        {/* chips do ponto */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="flex items-center gap-1.5 rounded-full bg-surface3 px-2.5 py-1 text-[0.72rem] font-medium text-[#3a3530]">
            <MapPin size={12} strokeWidth={2} />
            {ponto.talhaoRotulo ?? 'Talhão não identificado'}
          </span>
          {ponto.estadio && (
            <span className="rounded-full bg-[var(--accent-100)] px-2.5 py-1 text-[0.72rem] font-medium text-[var(--accent-900)]">
              Estádio {ponto.estadio}
            </span>
          )}
          <span className="rounded-full bg-surface3 px-2.5 py-1 text-[0.72rem] font-medium text-[#3a3530]">
            {`Ponto ${indice}`}
            {imgSrc ? (n > 1 ? ` · ${n} fotos` : '') : ' · sem foto'}
          </span>
        </div>

        <OcorrenciasCampoEditor itens={ponto.ocorrencias} onChange={onOcorrencias} />

        <p className="text-[0.69rem] font-medium uppercase tracking-[0.04em] text-text-2">
          Anotações{imgSrc && n > 1 ? ` · foto ${i + 1}/${n}` : ''}
        </p>
        <textarea
          value={principal?.recomendacao ?? ''}
          onChange={(e) => { setSalvo(false); if (principal) onRecomendacao(principal.clientUuid, e.target.value); }}
          placeholder="Anotações do agrônomo para este ponto…"
          rows={2}
          className="w-full resize-y rounded-[12px] border-[1.4px] border-[#727970] bg-transparent px-3 py-2.5 text-[0.875rem] text-text placeholder:text-text-2 focus:outline-none"
        />
        <button
          type="button"
          disabled={!principal}
          onClick={() => {
            if (!principal) return;
            void onSalvarAnotacao(principal.clientUuid, principal.recomendacao).then(() => setSalvo(true));
          }}
          className="flex items-center justify-center gap-1.5 self-end rounded-full bg-surface3 px-4 py-2 text-[0.8rem] font-semibold text-text active:scale-95 disabled:opacity-50"
        >
          <Check size={14} strokeWidth={2.25} /> {salvo ? 'Anotação salva' : 'Salvar anotação'}
        </button>

        {/* áudios: cada gravação vira mais um áudio da foto */}
        {principal && principal.audios.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {principal.audios.map((a, k) => (
              <div key={a.clientUuid} className="flex items-center gap-2 rounded-[10px] bg-surface3 px-3 py-2">
                <Mic size={14} strokeWidth={2} className="shrink-0 text-[#1d5a33]" />
                <span className="flex-1 text-[0.78rem] text-text">
                  Áudio {k + 1}
                  {a.durMs ? ` · ${Math.round(a.durMs / 1000)}s` : ''}
                </span>
                <button
                  type="button"
                  aria-label="Remover áudio"
                  onClick={() => onRemoverAudio(principal.clientUuid, a.clientUuid)}
                  className="flex size-7 items-center justify-center rounded-full text-[var(--danger)] active:scale-90"
                >
                  <X size={14} strokeWidth={2.25} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => principal && onAudio(principal.clientUuid)}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-surface3 py-3 text-[0.875rem] font-medium text-[#3a3530]"
        >
          {gravando ? (
            <>
              <Square size={17} strokeWidth={2} className="text-[var(--danger)]" /> Parar gravação
            </>
          ) : (
            <>
              <Mic size={18} strokeWidth={2} />
              {principal && principal.audios.length > 0 ? 'Gravar mais um áudio' : 'Gravar áudio'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Ocorrências a campo: só PRAGA, com a quantidade contada no ponto. Vários
// apontamentos no mesmo talhão viram soma e média no relatório.
function OcorrenciasCampoEditor({
  itens,
  onChange,
}: {
  itens: OcorrenciaCampo[];
  onChange: (itens: OcorrenciaCampo[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [qtd, setQtd] = useState('');

  function adicionar() {
    const n = nome.trim();
    if (!n) return;
    const valor = qtd.trim() ? Number(qtd.replace(',', '.')) : null;
    onChange([
      ...itens,
      {
        clientUuid: crypto.randomUUID(),
        tipo: 'praga',
        nome: n,
        quantidade: valor != null && !Number.isNaN(valor) ? valor : null,
        unidade: null,
        severidade: null,
      },
    ]);
    setNome('');
    setQtd('');
    setAberto(false);
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[0.69rem] font-medium uppercase tracking-[0.04em] text-text-2">Pragas no ponto</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {itens.map((o) => (
          <span
            key={o.clientUuid}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#f7e6c9] py-[3px] pl-2.5 pr-2 text-[0.72rem] font-medium text-[#6a4a17]"
          >
            {o.nome}
            {o.quantidade != null ? ` · ${o.quantidade}` : ''}
            <button
              type="button"
              aria-label="Remover"
              onClick={() => onChange(itens.filter((x) => x.clientUuid !== o.clientUuid))}
              className="active:scale-90"
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="rounded-full border border-dashed border-[#bdb9b4] py-[3px] pl-2.5 pr-3 text-[0.72rem] font-medium text-text-2 active:scale-95"
        >
          + praga
        </button>
      </div>
      {aberto && (
        <div className="flex items-center gap-2">
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Praga (ex: percevejo)"
            className={`${inputCls} flex-1`}
          />
          <input
            value={qtd}
            onChange={(e) => setQtd(e.target.value)}
            inputMode="decimal"
            placeholder="qtd"
            className={`${inputCls} w-[74px]`}
          />
          <button
            type="button"
            onClick={adicionar}
            className="rounded-full bg-surface3 px-3 py-2 text-[0.78rem] font-semibold text-text active:scale-95"
          >
            OK
          </button>
        </div>
      )}
    </div>
  );
}
